// EXTERNAL DEPENDENCIES
import React from 'react'
import { useDispatch } from 'react-redux'

// COMPONENTS
import NextAndBackButtons from '@/common/components/ui/next-and-back-buttons'

// HELPERS
import { focusOnErroredFormElement } from '@/utils/accessibility'

// REDUX
import { updateCurrentPanelIndexInEditForm } from '@/redux/features'
import { useBusinessProfileReduxState } from '@/screens/business-profile-page/hooks'

// CONSTANTS
import { reviewPanelId } from '@/screens/business-profile-page/constants/panel-ids'

// TYPES
import { NavigationButtonProps } from '@/types'

/**
 * Displays the back and next buttons that handle validation and navigation
 */
export function NavigationButtons({
  nextButtonProps,
  handleSubmit,
  backButtonProps,
  optionalLink,
  lastPanelIndex = reviewPanelId,
  pageIsValidated,
  isFormValid,
}: Readonly<{
  handleSubmit?: () => void
  nextButtonProps: Partial<NavigationButtonProps>
  backButtonProps?: Partial<NavigationButtonProps>
  optionalLink?: React.ReactNode
  lastPanelIndex?: number
  pageIsValidated?: boolean
  isFormValid: boolean
}>) {
  const dispatch = useDispatch()

  const {
    editedProfileData: { currentPanelIndex },
  } = useBusinessProfileReduxState()

  const isOnLastPanel = currentPanelIndex === lastPanelIndex
  const isOnFirstPanel = currentPanelIndex === 0

  const handleNavigation = (nextPanelIndex: number) =>
    dispatch(updateCurrentPanelIndexInEditForm(nextPanelIndex))

  function goToNextPanel() {
    handleNavigation(
      currentPanelIndex + 1 > lastPanelIndex
        ? currentPanelIndex
        : currentPanelIndex + 1,
    )
  }

  const handleNextClick = (e: React.FormEvent<HTMLFormElement>) => {
    focusOnErroredFormElement(e)

    if (isFormValid) {
      if (isOnLastPanel) {
        return handleSubmit()
      }

      goToNextPanel()
    }
  }

  const handleBackClick = () => {
    const previousPanelIndex = isOnFirstPanel ? 0 : currentPanelIndex - 1

    isOnFirstPanel && backButtonProps.handleClick
      ? backButtonProps.handleClick()
      : handleNavigation(previousPanelIndex)
  }

  return (
    <NextAndBackButtons
      nextButton={
        {
          handleClick: handleNextClick,
          ...nextButtonProps,
        } as NavigationButtonProps
      }
      backButton={
        {
          handleClick: handleBackClick,
        } as NavigationButtonProps
      }
      optionalLink={optionalLink}
      pageIsValidated={pageIsValidated}
    />
  )
}
